import { useEffect, useState, useCallback } from "react";
import { api, fmtDate, fmtNum, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Drop, Lightning } from "@phosphor-icons/react";
import { PageLoading } from "@/components/layout/Layout";
import { useT } from "@/contexts/I18nContext";
import { ListState } from "@/components/ListState";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Milk() {
    const { t } = useT();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ rfid: "", liters: "", timestamp: "" });
    const [saving, setSaving] = useState(false);

    const [closeOpen, setCloseOpen] = useState(false);
    const [closeForm, setCloseForm] = useState({ rfid: "", production_system: "", diet_type: "" });
    const [closing, setClosing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/milk", { params: { limit: 200 } });
            setItems(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/milk", {
                rfid: form.rfid,
                liters: Number(form.liters),
                timestamp: form.timestamp ? new Date(form.timestamp).toISOString() : null,
            });
            toast.success(t("milk_registered"));
            setOpen(false);
            setForm({ rfid: "", liters: "", timestamp: "" });
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const closeCycle = async (e) => {
        e.preventDefault();
        setClosing(true);
        try {
            await api.post("/milk/close", {
                rfid: closeForm.rfid,
                production_system: closeForm.production_system || null,
                diet_type: closeForm.diet_type || null,
            });
            toast.success(t("cycle_closed"));
            setCloseOpen(false);
            setCloseForm({ rfid: "", production_system: "", diet_type: "" });
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setClosing(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="milk-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{t("milk_title")}</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t("milk_subtitle")}</p>
                </div>
                <div className="flex gap-2">
                    <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" className="border-[#4A6E00] text-[#4A6E00] hover:bg-[#4A6E00]/10 hover:text-[#4A6E00]" data-testid="close-milk-cycle-btn">
                                <Lightning size={16} className="mr-2" weight="fill" /> {t("close_cycle")}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle className="font-display">{t("close_cycle_title")}</DialogTitle>
                                <DialogDescription>{t("close_cycle_desc")}</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={closeCycle} className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label>{t("animal_rfid")}</Label>
                                    <Input required value={closeForm.rfid} onChange={(e) => setCloseForm({ ...closeForm, rfid: e.target.value })} data-testid="close-milk-rfid-input" />
                                </div>
                                <div className="border-t pt-4 space-y-3">
                                    <div className="text-xs text-muted-foreground">{t("period_override")}</div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">{t("production_system")}</Label>
                                        <Select value={closeForm.production_system} onValueChange={(v) => setCloseForm({ ...closeForm, production_system: v })}>
                                            <SelectTrigger data-testid="close-milk-system-select"><SelectValue placeholder="—" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="feedlot">{t("sys_feedlot")}</SelectItem>
                                                <SelectItem value="semi">{t("sys_semi")}</SelectItem>
                                                <SelectItem value="pasture">{t("sys_pasture")}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">{t("diet_type_optional")}</Label>
                                        <Input
                                            value={closeForm.diet_type}
                                            onChange={(e) => setCloseForm({ ...closeForm, diet_type: e.target.value })}
                                            placeholder={t("diet_placeholder")}
                                            data-testid="close-milk-diet-input"
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button type="submit" disabled={closing} className="bg-[#083D1E] hover:bg-[#052A14] text-white" data-testid="close-milk-confirm-btn">
                                        {closing ? t("saving") : t("confirm_close")}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[#083D1E] hover:bg-[#052A14] text-white" data-testid="new-milk-btn">
                                <Plus size={16} className="mr-2" /> {t("new_milk")}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle className="font-display">{t("new_milk_title")}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label>{t("animal_rfid")}</Label>
                                    <Input required value={form.rfid} onChange={(e) => setForm({ ...form, rfid: e.target.value })} data-testid="milk-rfid-input" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>{t("liters_label")}</Label>
                                    <Input
                                        type="number"
                                        step="0.1"
                                        min="0.1"
                                        required
                                        value={form.liters}
                                        onChange={(e) => setForm({ ...form, liters: e.target.value })}
                                        data-testid="milk-liters-input"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>{t("datetime_optional")}</Label>
                                    <Input
                                        type="datetime-local"
                                        value={form.timestamp}
                                        onChange={(e) => setForm({ ...form, timestamp: e.target.value })}
                                        data-testid="milk-ts-input"
                                    />
                                </div>
                                <DialogFooter>
                                    <Button type="submit" disabled={saving} className="bg-[#083D1E] hover:bg-[#052A14] text-white" data-testid="milk-save-btn">
                                        {saving ? t("saving") : t("register")}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Card className="card-paper shadow-none overflow-hidden">
                <ListState
                    loading={loading}
                    isEmpty={items.length === 0}
                    emptyIcon={Drop}
                    emptyTitle={t("no_milk")}
                    emptyHint={t("register_first_milk")}
                >
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("date")}</TableHead>
                                <TableHead>{t("rfid")}</TableHead>
                                <TableHead>{t("origin")}</TableHead>
                                <TableHead className="text-right">{t("liters")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((m) => (
                                <TableRow key={m.id} data-testid={`milk-row-${m.id}`}>
                                    <TableCell>{fmtDate(m.timestamp)}</TableCell>
                                    <TableCell className="font-medium">{m.rfid}</TableCell>
                                    <TableCell>
                                        <Badge variant={m.source === "parlour" ? "default" : "secondary"} className={m.source === "parlour" ? "bg-[#083D1E] text-white hover:bg-[#083D1E]" : ""}>
                                            {m.source === "parlour" ? t("origin_parlour") : t("origin_manual")}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right metric-number">{fmtNum(m.liters, 2)} L</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ListState>
            </Card>
        </div>
    );
}
